import { clsx } from 'clsx';
import { Check, Copy, Loader2, Search, X } from 'lucide-react';
import {
  useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

export { clsx as cx };

/* Buttons ------------------------------------------------------------------------------- */

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm',
  secondary: 'bg-white text-slate-800 ring-1 ring-slate-300 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:hover:bg-slate-800',
  danger: 'bg-red-600 text-white hover:bg-red-700 shadow-sm',
  ghost: 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
};

const buttonClass = (variant: Variant, size: 'sm' | 'md' | 'lg', className?: string) =>
  clsx(
    'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
    size === 'sm' && 'h-8 px-3 text-sm',
    size === 'md' && 'h-10 px-4 text-sm',
    size === 'lg' && 'h-12 px-5 text-base',
    VARIANTS[variant],
    className,
  );

/** A link that looks like a button (never nest a <button> inside a link). */
export function ButtonLink({ to, variant = 'primary', size = 'md', icon, children, className }: {
  to: string; variant?: Variant; size?: 'sm' | 'md' | 'lg'; icon?: ReactNode; children: ReactNode; className?: string;
}) {
  return <Link to={to} className={buttonClass(variant, size, className)}>{icon}{children}</Link>;
}

export function Button({
  variant = 'primary', size = 'md', loading, icon, children, className, disabled, ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' | 'lg'; loading?: boolean; icon?: ReactNode }) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      className={buttonClass(variant, size, className)}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
}

/* Form fields --------------------------------------------------------------------------- */

export function Field({ label, hint, error, children, required, htmlFor }: {
  label: string; hint?: ReactNode; error?: string; children: ReactNode; required?: boolean; htmlFor?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-800 dark:text-slate-200">
        {label}
        {required && <span className="ml-0.5 text-red-600" aria-hidden>*</span>}
      </label>
      {children}
      {error ? (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : hint ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{hint}</p>
      ) : null}
    </div>
  );
}

const inputClass = (error?: string) =>
  clsx(
    'block w-full rounded-lg border bg-white px-3 text-base text-slate-900 shadow-sm placeholder:text-slate-400 sm:text-sm',
    'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 dark:bg-slate-900 dark:text-slate-100',
    error ? 'border-red-400' : 'border-slate-300 dark:border-slate-700',
  );

/** Serial number at the start of a list row (S.No). */
export function SNo({ n, className }: { n: number; className?: string }) {
  return <span className={clsx('w-7 shrink-0 text-right text-xs text-slate-400 tabular-nums', className)} aria-label={`Row ${n}`}>{n}</span>;
}

/** For small inputs and selects placed inline (filters), without a label above. */
export const inlineInputClass = 'h-9 rounded-lg border border-slate-300 bg-white px-2.5 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 dark:border-slate-700 dark:bg-slate-900';

type FieldProps = { label: string; hint?: ReactNode; error?: string };

export function TextField({ label, hint, error, className, ...rest }: FieldProps & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} required={rest.required} htmlFor={id}>
      <input id={id} {...rest} aria-invalid={!!error} className={clsx(inputClass(error), 'h-10', className)} />
    </Field>
  );
}

export function SelectField({ label, hint, error, children, ...rest }: FieldProps & SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} required={rest.required} htmlFor={id}>
      <select id={id} {...rest} aria-invalid={!!error} className={clsx(inputClass(error), 'h-10 pr-8')}>
        {children}
      </select>
    </Field>
  );
}

export function TextAreaField({ label, hint, error, ...rest }: FieldProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} required={rest.required} htmlFor={id}>
      <textarea id={id} rows={3} {...rest} aria-invalid={!!error} className={clsx(inputClass(error), 'py-2')} />
    </Field>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Search…' }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" aria-hidden />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className={clsx(inputClass(), 'h-10 pl-9')}
      />
    </div>
  );
}

/* Surfaces ------------------------------------------------------------------------------ */

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={clsx('rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900', className)}>
      {children}
    </div>
  );
}

export function PageHeader({ title, description, actions }: { title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-slate-600 dark:text-slate-400">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

const BADGE_TONES = {
  gray: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-400',
  amber: 'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-400',
  red: 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-500/10 dark:text-red-400',
  brand: 'bg-brand-50 text-brand-700 ring-brand-600/20',
};
export function Badge({ tone = 'gray', children }: { tone?: keyof typeof BADGE_TONES; children: ReactNode }) {
  return <span className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ring-transparent', BADGE_TONES[tone])}>{children}</span>;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('animate-pulse rounded-md bg-slate-200 dark:bg-slate-800', className)} aria-hidden />;
}

export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-4" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} className="h-10 w-full" />)}
    </div>
  );
}

export function EmptyState({ icon, title, description, action }: { icon: ReactNode; title: string; description: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">{icon}</div>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="mt-1 max-w-md text-slate-600 dark:text-slate-400">{description}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center" role="alert">
      <p className="font-medium">We couldn’t load this.</p>
      <p className="mt-1 text-slate-600 dark:text-slate-400">{error instanceof Error ? error.message : 'Please try again.'}</p>
      {onRetry && <Button variant="secondary" className="mt-4" onClick={onRetry}>Try again</Button>}
    </div>
  );
}

/* Overlays (native <dialog>: Esc to close, focus handled by the browser) ------------------ */

function useDialog(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const handler = (e: Event) => { e.preventDefault(); onClose(); };
    d.addEventListener('cancel', handler);
    return () => d.removeEventListener('cancel', handler);
  }, [onClose]);
  return ref;
}

export function Modal({ open, onClose, title, children, footer, size = 'md' }: {
  open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; size?: 'sm' | 'md' | 'lg';
}) {
  const ref = useDialog(open, onClose);
  return (
    <dialog
      ref={ref}
      onClick={(e) => e.target === ref.current && onClose()}
      className={clsx(
        'm-auto w-[calc(100%-2rem)] rounded-2xl bg-white p-0 text-slate-900 shadow-2xl backdrop:bg-slate-900/50 backdrop:backdrop-blur-[2px] dark:bg-slate-900 dark:text-slate-100',
        size === 'sm' && 'max-w-md', size === 'md' && 'max-w-lg', size === 'lg' && 'max-w-2xl',
      )}
    >
      {open && (
        <div className="flex max-h-[85vh] flex-col">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
            <h2 className="text-lg font-semibold">{title}</h2>
            <button onClick={onClose} className="rounded-md p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Close">
              <X className="size-5" />
            </button>
          </div>
          <div className="overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3 dark:border-slate-800">{footer}</div>}
        </div>
      )}
    </dialog>
  );
}

/** Side panel for forms. Full screen on phones. */
export function Drawer({ open, onClose, title, description, children, footer, wide, headerExtra }: {
  open: boolean; onClose: () => void; title: ReactNode; description?: ReactNode; children: ReactNode; footer?: ReactNode;
  wide?: boolean; headerExtra?: ReactNode;
}) {
  const ref = useDialog(open, onClose);
  return (
    <dialog
      ref={ref}
      onClick={(e) => e.target === ref.current && onClose()}
      className={clsx(
        'm-0 ml-auto h-dvh max-h-dvh w-full bg-white p-0 text-slate-900 shadow-2xl backdrop:bg-slate-900/40 dark:bg-slate-900 dark:text-slate-100',
        wide ? 'max-w-3xl' : 'max-w-lg',
      )}
    >
      {open && (
        <div className="flex h-full flex-col">
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold">{title}</h2>
              {description && <div className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">{description}</div>}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {headerExtra}
              <button onClick={onClose} className="rounded-md p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Close">
                <X className="size-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
          {footer && <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3 dark:border-slate-800">{footer}</div>}
        </div>
      )}
    </dialog>
  );
}

export function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel, danger, loading }: {
  open: boolean; onClose: () => void; onConfirm: () => void; title: string; message: ReactNode;
  confirmLabel: string; danger?: boolean; loading?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      <div className="text-slate-700 dark:text-slate-300">{message}</div>
    </Modal>
  );
}

/** Shows a secret once (e.g. a temporary password) with one-tap copy. */
export function CopyBox({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Couldn’t copy automatically. Please select the text and copy it.');
    }
  };
  return (
    <div>
      <p className="mb-1 text-sm font-medium text-slate-700 dark:text-slate-300">{label}</p>
      <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-50 p-2 pl-3 dark:border-slate-700 dark:bg-slate-800">
        <code className="flex-1 font-mono text-base break-all select-all">{value}</code>
        <Button size="sm" variant="secondary" onClick={copy} icon={copied ? <Check className="size-4 text-emerald-600" /> : <Copy className="size-4" />}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

/** A checkbox with a label and an optional explanation underneath. */
export function CheckRow({ label, hint, checked, onChange, disabled }: {
  label: string; hint?: ReactNode; checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean;
}) {
  return (
    <label className={clsx('flex items-start gap-3', disabled && 'opacity-60')}>
      <input type="checkbox" className="mt-0.5 size-5 shrink-0 rounded accent-brand-600" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-sm text-slate-500 dark:text-slate-400">{hint}</span>}
      </span>
    </label>
  );
}
